import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ResumeUploadSection from './ResumeUploadSection';
import ResumeScoreCard from './ResumeScoreCard';
import ResumeDataEditor from './ResumeDataEditor';
import ResumeFeedbackPanel from './ResumeFeedbackPanel';
import ResumeVersionHistory from './ResumeVersionHistory';
import { PATHS } from '../../utils/platformPaths';

const ResumeReviewPage = () => {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('upload'); // 'upload', 'review', 'history'
  const [currentReview, setCurrentReview] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch usage data on mount
  useEffect(() => {
    fetchUsageData();
  }, []);

  const fetchUsageData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate(PATHS.login);
        return;
      }

      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/alumni/resume/usage`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch usage data');
      }

      const data = await response.json();
      setUsageData(data);
    } catch (err) {
      console.error('Error fetching usage:', err);
      setError('Failed to load usage information');
    }
  };

  const handleUploadComplete = (reviewData) => {
    setCurrentReview(reviewData);
    setCurrentView('review');
    fetchUsageData(); // Refresh usage after upload
  };

  const handleViewHistory = () => {
    setCurrentView('history');
    setCurrentReview(null);
  };

  const handleViewReview = (review) => {
    setCurrentReview(review);
    setCurrentView('review');
  };

  const handleBackToUpload = () => {
    setCurrentView('upload');
    setCurrentReview(null);
    setError(null);
  };

  const handleDataCorrected = async (correctedData) => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/alumni/resume/review/${currentReview.id}/correct`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ corrected_data: correctedData })
      });

      if (!response.ok) {
        throw new Error('Failed to save corrections');
      }

      const updatedReview = await response.json();
      setCurrentReview(updatedReview);
    } catch (err) {
      console.error('Error saving corrections:', err);
      setError('Failed to save corrections');
    }
  };

  const handleFinalize = async () => {
    if (!currentReview) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/alumni/resume/review/${currentReview.id}/finalize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to finalize review');
      }

      const finalizedReview = await response.json();
      setCurrentReview(finalizedReview);
      alert('Resume review finalized! Your profile is now ready for job matching.');
    } catch (err) {
      console.error('Error finalizing review:', err);
      setError('Failed to finalize review');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          {/* Back Button */}
          <button
            onClick={() => navigate(PATHS.dashboard)}
            className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                AI Resume Review
              </h1>
              <p className="text-gray-600 mt-2">
                Get AI-powered feedback to improve your resume
              </p>
            </div>

            {/* Usage Information */}
            {usageData && (
              <div className="mt-4 sm:mt-0 sm:ml-6">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg p-4 text-white">
                  <div className="text-sm font-medium">Reviews Remaining</div>
                  <div className="text-3xl font-bold">
                    {usageData.unlimited_access ? (
                      <span className="flex items-center">
                        <svg className="w-8 h-8 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                        </svg>
                        Unlimited
                      </span>
                    ) : (
                      `${usageData.reviews_remaining} / 4`
                    )}
                  </div>
                  <div className="text-xs mt-1 opacity-90">
                    {usageData.unlimited_access ? 'Admin approved' : 'This month'}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Tabs */}
          <div className="mt-6 border-b border-gray-200">
            <nav className="flex space-x-8">
              <button
                onClick={handleBackToUpload}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentView === 'upload'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Upload Resume
              </button>
              <button
                onClick={() => currentReview && setCurrentView('review')}
                disabled={!currentReview}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentView === 'review'
                    ? 'border-blue-500 text-blue-600'
                    : currentReview
                    ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    : 'border-transparent text-gray-400 cursor-not-allowed'
                }`}
              >
                Review Results
              </button>
              <button
                onClick={handleViewHistory}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentView === 'history'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Version History
              </button>
            </nav>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError(null)}
                  className="text-red-400 hover:text-red-600"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="space-y-6">
          {currentView === 'upload' && (
            <ResumeUploadSection
              onUploadComplete={handleUploadComplete}
              usageData={usageData}
              onError={setError}
            />
          )}

          {currentView === 'review' && currentReview && (
            <>
              {/* Score Cards */}
              <ResumeScoreCard
                scores={{
                  overall_score: currentReview.overall_score,
                  ats_score: currentReview.ats_score,
                  content_quality_score: currentReview.content_quality_score,
                  completeness_score: currentReview.completeness_score,
                  format_score: currentReview.format_score
                }}
                status={currentReview.status}
              />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column: Data Editor */}
                <ResumeDataEditor
                  parsedData={currentReview.corrected_data || currentReview.parsed_data}
                  onSave={handleDataCorrected}
                  isFinalized={currentReview.status === 'finalized'}
                />

                {/* Right Column: Feedback */}
                <ResumeFeedbackPanel
                  recommendations={currentReview.recommendations}
                  missingFields={currentReview.missing_fields}
                  flaggedIssues={currentReview.flagged_issues}
                />
              </div>

              {/* Finalize Button */}
              {currentReview.status !== 'finalized' && (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Ready to finalize?
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Once finalized, this resume will be used for job matching. You can always upload a new version.
                      </p>
                    </div>
                    <button
                      onClick={handleFinalize}
                      disabled={loading}
                      className="ml-4 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Finalizing...' : 'Finalize Resume'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {currentView === 'history' && (
            <ResumeVersionHistory
              onViewReview={handleViewReview}
              onError={setError}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ResumeReviewPage;
